"use strict";
import { Logger } from "@exodus/ethersproject-logger";
import { version } from "./_version.js";
const logger = new Logger(version);
const hasOwn = Function.prototype.call.bind(Object.prototype.hasOwnProperty);
function objectGuard(object, arrays = false, allow = []) {
    if (!object || typeof (object) !== "object") {
        logger.throwArgumentError("invalid object", "object", object);
    }
    const proto = Object.getPrototypeOf(object);
    if (proto === Object.prototype)
        return;
    if (arrays && proto === Array.prototype && Array.isArray(object))
        return;
    if (proto && proto.constructor && allow.includes(proto.constructor.name))
        return;
    // Else, we throw
    logger.throwArgumentError("not a plain object", "object", object);
}
export function defineReadOnly(object, name, value) {
    Object.defineProperty(object, name, {
        enumerable: true,
        value: value,
        writable: false,
    });
}
// Crawl up the constructor chain to find a static method
export function getStatic(ctor, key) {
    for (let i = 0; i < 32; i++) {
        if (ctor[key]) {
            return ctor[key];
        }
        if (!ctor.prototype || typeof (ctor.prototype) !== "object") {
            break;
        }
        ctor = Object.getPrototypeOf(ctor.prototype).constructor;
    }
    return null;
}
export async function resolveProperties(object) {
    objectGuard(object);
    const promises = Object.entries(object).map(([key, value]) => {
        return Promise.resolve(value).then((v) => ([key, v]));
    });
    const results = await Promise.all(promises);
    return Object.fromEntries(results);
}
export function checkProperties(object, properties) {
    objectGuard(object, true);
    if (!object || typeof (object) !== "object") {
        logger.throwArgumentError("invalid object", "object", object);
    }
    Object.keys(object).forEach((key) => {
        if (!properties[key]) {
            logger.throwArgumentError("invalid object key - " + key, "transaction:" + key, object);
        }
    });
}
export function shallowCopy(object) {
    objectGuard(object);
    return Object.fromEntries(Object.entries(object));
}
const opaque = { bigint: true, boolean: true, "function": true, number: true, string: true };
function _isFrozen(object) {
    // Opaque objects are not mutable, so safe to copy by assignment
    if (object === undefined || object === null || opaque[typeof (object)]) {
        return true;
    }
    if (Array.isArray(object) || typeof (object) === "object") {
        if (!Object.isFrozen(object)) {
            return false;
        }
        objectGuard(object, true, ['FunctionFragment', 'BigNumber', 'ParamType']);
        // NOTE: this checks only own properties!
        const keys = Object.keys(object);
        for (let i = 0; i < keys.length; i++) {
            let value = null;
            try {
                value = object[keys[i]];
            }
            catch (error) {
                // If accessing a value triggers an error, it is a getter
                // designed to do so (e.g. Result) and is therefore "frozen"
                continue;
            }
            if (!_isFrozen(value)) {
                return false;
            }
        }
        return true;
    }
    return logger.throwArgumentError(`Cannot deepCopy ${typeof (object)}`, "object", object);
}
// Returns a new copy of object, such that no properties may be replaced.
// New properties may be added only to objects.
function _deepCopy(object) {
    if (_isFrozen(object)) {
        return object;
    }
    // Arrays are mutable, so we need to create a copy
    if (Array.isArray(object)) {
        return Object.freeze(object.map((item) => deepCopy(item)));
    }
    if (typeof (object) === "object") {
        objectGuard(object, true, ['FunctionFragment']);
        const result = {};
        // NOTE: this cloned not only own properties!
        // We added a check so it can only either clone own properties and copy prototype functions
        // Only FunctionFragment non-plain object seems to be passed here though, which has only `constructor` and `format` methods.
        for (const key in object) {
            // We allow to inherit only functions from the prototype chain, those are returned as-is as _isFrozen returns true
            if (!hasOwn(object, key) && typeof object[key] !== 'function') {
                logger.throwArgumentError("Unexpected: trying to copy a non-own property in deepCopy()", "object", object);
            }
            const value = object[key];
            if (value === undefined) {
                continue;
            }
            defineReadOnly(result, key, deepCopy(value));
        }
        return result;
    }
    return logger.throwArgumentError(`Cannot deepCopy ${typeof (object)}`, "object", object);
}
export function deepCopy(object) {
    return _deepCopy(object);
}
export class Description {
    constructor(info) {
        objectGuard(info);
        for (const key in info) {
            if (!hasOwn(info, key)) {
                logger.throwArgumentError("Unexpected: trying to copy a non-own property in Description()", "object", info);
            }
            // Check if the key already exists in this or in prototype chain, and we are in the deepest constructor. Block to ensure we don't hit a setter
            if (key in this)
                logger.throwArgumentError(`Cannot assign in Description to an existing property`, "key", key);
            this[key] = deepCopy(info[key]);
        }
    }
}
